/**
 * Precedência do destino (módulo puro — sem IA, sem Maps, sem créditos).
 *
 * Regra vinculativa e genérica (sem cidades especiais):
 *  - "quero ir a X", "vou a X", "visitar X" (e equivalentes EN/ES/FR/IT/DE)
 *    definem X como DESTINO EXPLÍCITO;
 *  - um destino explícito prevalece SEMPRE sobre GPS, IP, cache, cidade
 *    detetada e sobre o campo `startFromUserLocation` devolvido pela IA;
 *  - "estou em X", "explorar aqui", "usar a minha localização" (e equivalentes)
 *    autorizam a localização atual como destino;
 *  - o GPS continua disponível para distância e "Navegar até ao ponto de
 *    partida", mas nunca substitui um destino escrito;
 *  - havendo ambiguidade real (texto nomeia uma cidade diferente da escolhida
 *    manualmente), perguntar inline — de graça — antes de IA/Maps/créditos.
 */
import { extractDestinationFromPrompt } from "./destination-extraction";
import { extractExplicitLocality } from "./geo-identity";
import { comparableCityName } from "./city-endonym";


const fold = (value: string) =>
  (value ?? "").normalize("NFD").replace(/[\u0300-\u036f]/gu, "").toLowerCase().trim();

/** "estou em…", "aqui", "usar a minha localização" — multilingue. */
const CURRENT_LOCATION_PATTERNS: RegExp[] = [
  // PT
  /\b(?:estou|estamos|encontro-me|encontramo-nos|ja\s+cheguei|acabei\s+de\s+chegar)\s+(?:em|no|na|aqui|perto|junto)\b/u,
  /\b(?:explorar|descobrir|ver|visitar)\s+aqui\b/u,
  /\b(?:a\s+partir\s+de\s+onde\s+estou|daqui|usar\s+a\s+minha\s+localizacao|minha\s+localizacao|onde\s+estou)\b/u,
  // EN
  /\b(?:i\s+am|i'm|we\s+are|we're)\s+(?:in|at|near|here)\b/u,
  /\b(?:use\s+my\s+(?:current\s+)?location|my\s+current\s+location|where\s+i\s+am|explore\s+here|around\s+me|near\s+me)\b/u,
  // ES
  /\b(?:estoy|estamos)\s+(?:en|cerca|aqui)\b/u,
  /\b(?:usar\s+mi\s+ubicacion|mi\s+ubicacion|donde\s+estoy|explorar\s+aqui)\b/u,
  // FR
  /\b(?:je\s+suis|nous\s+sommes|on\s+est)\s+(?:a|au|dans|pres|ici)\b/u,
  /\b(?:ma\s+position|utiliser\s+ma\s+position|ou\s+je\s+suis|explorer\s+ici)\b/u,
  // IT
  /\b(?:sono|siamo)\s+(?:a|in|vicino|qui)\b/u,
  /\b(?:la\s+mia\s+posizione|usa\s+la\s+mia\s+posizione|dove\s+sono|esplorare\s+qui)\b/u,
  // DE
  /\b(?:ich\s+bin|wir\s+sind)\s+(?:in|bei|hier)\b/u,
  /\b(?:mein\s+standort|meinen\s+standort|wo\s+ich\s+bin|hier\s+erkunden)\b/u,
];

/** O texto pede explicitamente para usar a localização atual? */
export function promptWantsCurrentLocation(prompt: string): boolean {
  const text = fold(prompt);
  if (!text) return false;
  return CURRENT_LOCATION_PATTERNS.some((pattern) => pattern.test(text));
}

export type DestinationInput = {
  /** Texto livre do pedido. */
  prompt: string;
  /** Cidade devolvida pela IA (pode estar errada ou vazia). */
  parsedCity?: string | null;
  /** Sinal da IA para começar no GPS (nunca vinculativo). */
  parsedStartFromUserLocation?: boolean | null;
  /** Cidade atualmente no campo/estado da aplicação (GPS, IP, cache…). */
  currentCity?: string | null;
  /** True quando essa cidade foi escolhida manualmente pelo utilizador. */
  currentCityUserSelected?: boolean;
  /** True quando o utilizador carregou em "Usar a minha localização". */
  userChoseCurrentLocation?: boolean;
};

export type DestinationDecision = {
  /** Cidade a usar como destino (undefined ⇒ manter a atual). */
  city?: string;
  /** Origem do destino escolhido. */
  source: "prompt_explicit" | "current_location" | "manual_field" | "ai_parsed" | "unknown";
  /** O GPS pode ser usado como destino/origem do roteiro? */
  useCurrentLocation: boolean;
  /** Há um conflito real que exige uma pergunta gratuita antes de gerar. */
  ambiguous: boolean;
  /** Cidades em conflito, quando `ambiguous`. */
  conflict?: { promptCity: string; currentCity: string };
};

export function resolveDestinationPrecedence(input: DestinationInput): DestinationDecision {
  const named = extractDestinationFromPrompt(input.prompt ?? "");
  const promptCity = named.explicit ? named.city : undefined;
  const wantsHere = promptWantsCurrentLocation(input.prompt ?? "");

  // 1. Destino escrito: prevalece sobre GPS/IP/cache e sobre a IA.
  if (promptCity) {
    const current = (input.currentCity ?? "").trim();
    const conflicting =
      !!current &&
      !!input.currentCityUserSelected &&
      comparableCityName(current) !== comparableCityName(promptCity);
    return {
      city: promptCity,
      // "Estou em Sintra": o destino é Sintra e o GPS continua a servir de
      // ponto de partida. "Vou a Sintra": o GPS nunca é destino.
      source: wantsHere ? "current_location" : "prompt_explicit",
      useCurrentLocation: wantsHere,
      ambiguous: conflicting,
      conflict: conflicting ? { promptCity, currentCity: current } : undefined,
    };
  }


  // 2. Escolha manual de "Usar a minha localização" ou texto "estou em…".
  if (input.userChoseCurrentLocation || wantsHere) {
    // "Estou em X" nomeia a localidade: o destino é X (o GPS serve apenas
    // como ponto de partida). Regra genérica, sem cidades especiais.
    const locality = extractExplicitLocality(input.prompt ?? "");
    return {
      city: locality,
      source: "current_location",
      useCurrentLocation: true,
      ambiguous: false,
    };
  }


  // 3. Campo escolhido manualmente pelo utilizador.
  if (input.currentCity && input.currentCityUserSelected) {
    return {
      city: input.currentCity,
      source: "manual_field",
      useCurrentLocation: false,
      ambiguous: false,
    };
  }

  // 4. Só então a sugestão da IA (que pode pedir GPS).
  const aiCity = (input.parsedCity ?? "").trim();
  if (aiCity) {
    return {
      city: aiCity,
      source: "ai_parsed",
      useCurrentLocation: !!input.parsedStartFromUserLocation,
      ambiguous: false,
    };
  }

  return {
    source: "unknown",
    useCurrentLocation: !!input.parsedStartFromUserLocation,
    ambiguous: false,
  };
}

const QUESTIONS: Record<string, (a: string, b: string) => string> = {
  pt: (a, b) => `Queres o roteiro em ${a} ou em ${b}?`,
  en: (a, b) => `Do you want the itinerary in ${a} or in ${b}?`,
  es: (a, b) => `¿Quieres la ruta en ${a} o en ${b}?`,
  fr: (a, b) => `Veux-tu l'itinéraire à ${a} ou à ${b} ?`,
  it: (a, b) => `Vuoi l'itinerario a ${a} o a ${b}?`,
  de: (a, b) => `Möchtest du die Route in ${a} oder in ${b}?`,
};

/** Pergunta inline (gratuita) para o conflito destino escrito vs. cidade atual. */
export function destinationAmbiguityQuestion(
  decision: DestinationDecision,
  language = "pt",
): string | null {
  if (!decision.ambiguous || !decision.conflict) return null;
  const build = QUESTIONS[language.slice(0, 2).toLowerCase()] ?? QUESTIONS.en;
  return build(decision.conflict.promptCity, decision.conflict.currentCity);
}
